import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link, Box, Badge, CircularProgress } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { AccreditationContext } from '../context';
import { AppContext } from 'views/App/context';
import { QuickSearch } from 'components/_atoms';
import useAccreditationBodies from '../_data_sources/AccreditationBodies/useAccreditationBodies';
import { AccreditationBody } from '../../_types/awb';
import useUrlParams from 'hooks/useUrlParams';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import '../../utils/cleanBodies';
import { ServerErrorResponse } from 'data_sources/constants';
import { getAccreditationVersionsAvailable } from '../../_data_sources/Settings/useAccreditationVersionsAvailable';

type AccreditingBodiesListParams = {
  search?: string | null;
};

const defaultParams: AccreditingBodiesListParams = {
  search: null,
};

const AccreditingBodiesList: React.FC = () => {
  const {} = useContext(AccreditationContext);
  const { currentOrganizationId } = useContext(AppContext);
  const navigate = useNavigate();
  const [params, setParams] = useUrlParams(defaultParams);
  const [accreditingBodies, setAccreditingBodies] = useState<AccreditationBody[]>([]);
  const [loadingBodyId, setLoadingBodyId] = useState<string | null>(null);

  const updateParams = useCallback(
    (updatedParams: AccreditingBodiesListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const newParams = { ...params };
    return newParams;
  }, [params]);

  const gridViewQuery = useAccreditationBodies<AccreditationBody>(
    currentOrganizationId,
    queryParams,
    {},
    (errors: ServerErrorResponse) => {
      console.error('Error fetching accrediting bodies:', errors);
    }
  );

  useEffect(() => {
    if (gridViewQuery.data && !gridViewQuery.isError) {
      const fetchedAccreditingBodies = gridViewQuery.data.pages.flatMap((page) => page.content) || [];
      setAccreditingBodies(fetchedAccreditingBodies);
    }
  }, [gridViewQuery.data, gridViewQuery.isError]);

  const handleBodyClick = useCallback(
    async (e: React.MouseEvent, bodyId: string) => {
      e.preventDefault();
      setLoadingBodyId(bodyId);

      try {
        const versionsData = await getAccreditationVersionsAvailable(currentOrganizationId || '');
        const enabledVersions = versionsData.content.filter(
          (v) => v.accreditationBodyId === bodyId && v.accepted === 'ENABLED'
        );

        if (enabledVersions.length === 1) {
          navigate(`./${bodyId}/versions/${enabledVersions[0].versionId}/standards`);
        } else {
          navigate(`./${bodyId}/versions`);
        }
      } catch (error) {
        console.error('Error fetching versions:', error);
        navigate(`./${bodyId}/versions`);
      } finally {
        setLoadingBodyId(null);
      }
    },
    [currentOrganizationId, navigate]
  );

  const columns: ColumnDef = useMemo(
    () => ({
      abbreviation: {
        header: t('awb.accreditingBodies.properties.abbreviation'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const isLoading = loadingBodyId === row.id;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Link
                id={`accrediting_body_${row.id}`}
                href={`./${row.id}/versions`}
                onClick={(e) => handleBodyClick(e, row.id)}
                sx={{ pointerEvents: isLoading ? 'none' : 'auto' }}
              >
                {row.abbreviation}
              </Link>
              {isLoading && <CircularProgress size={14} />}
            </Box>
          );
        },
      },
      name: {
        header: t('awb.accreditingBodies.properties.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.name}</span>;
        },
      },
    }),
    [t, loadingBodyId, handleBodyClick]
  );

  const gridViewQueryData = useMemo(
    () => ({
      hasNextPage: gridViewQuery.hasNextPage,
      isFetchingNextPage: gridViewQuery.isFetchingNextPage,
      fetchNextPage: gridViewQuery.fetchNextPage,
      isError: gridViewQuery.isError,
      isLoading: gridViewQuery.isLoading,
      error: gridViewQuery.error,
      data: {
        pages:
          gridViewQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [gridViewQuery]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('awb.accreditingBodies.title'),
        subtitle: t('awb.accreditingBodies.subtitle'),
        titleBadge:
          (gridViewQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={gridViewQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='keyword_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val })}
              placeholder={t('awb.accreditingBodies.search.placeholder')}
            />
          }
        />
        <GridSection
          columns={columns}
          data={accreditingBodies}
          query={gridViewQueryData}
        />
      </Box>
    </CommonLayout>
  );
};

export default AccreditingBodiesList;
